/**
 * Style Genome — schema (data-driven field spec).
 *
 * The genome contract expressed as DATA, not code: `GENOME_FIELDS` lists every
 * dimension, the vocabulary it draws from, whether it is a single value or a
 * list, and whether it is required. The validator iterates this table, so
 * adding a dimension is a one-line data change — no per-field logic anywhere.
 */

import * as V from './vocabulary';

export const GENOME_SCHEMA_ID = 'genome/1';
export const GENOME_VERSION = 1;

/**
 * @typedef {Object} GenomeFieldSpec
 * @property {string} key
 * @property {ReadonlyArray<string>} vocab   Allowed values.
 * @property {boolean} list                   True → array of vocab values.
 * @property {boolean} required
 */

/** The 25 design-language dimensions, in canonical order. */
export const GENOME_FIELDS = Object.freeze([
  { key: 'visualPhilosophy', vocab: V.VISUAL_PHILOSOPHY, list: false, required: true },
  { key: 'composition', vocab: V.COMPOSITION, list: false, required: true },
  { key: 'layoutPhilosophy', vocab: V.LAYOUT_PHILOSOPHY, list: false, required: true },
  { key: 'grid', vocab: V.GRID, list: false, required: true },
  { key: 'rhythm', vocab: V.RHYTHM, list: false, required: true },
  { key: 'spacing', vocab: V.SPACING, list: false, required: true },
  { key: 'typography', vocab: V.TYPOGRAPHY, list: false, required: true },
  { key: 'color', vocab: V.COLOR, list: false, required: true },
  { key: 'shape', vocab: V.SHAPE, list: false, required: true },
  { key: 'decoration', vocab: V.DECORATION, list: false, required: true },
  { key: 'photography', vocab: V.PHOTOGRAPHY, list: false, required: true },
  { key: 'illustration', vocab: V.ILLUSTRATION, list: false, required: true },
  { key: 'icon', vocab: V.ICON, list: false, required: true },
  { key: 'effects', vocab: V.EFFECTS, list: true, required: true },
  { key: 'cta', vocab: V.CTA, list: false, required: true },
  { key: 'hero', vocab: V.HERO, list: false, required: true },
  { key: 'card', vocab: V.CARD, list: false, required: true },
  { key: 'border', vocab: V.BORDER, list: false, required: true },
  { key: 'background', vocab: V.BACKGROUND, list: false, required: true },
  { key: 'premiumCues', vocab: V.PREMIUM_CUES, list: true, required: true },
  { key: 'avoid', vocab: V.AVOID, list: true, required: true },
  { key: 'componentFamilies', vocab: V.COMPONENT_FAMILIES, list: true, required: true },
  { key: 'libraryPatterns', vocab: V.LIBRARY_PATTERNS, list: true, required: true },
  { key: 'diversityTags', vocab: V.DIVERSITY_TAGS, list: true, required: true },
]);

/** Expansion rules — semantic seeds the Thinking Engine maps to V2 tokens. */
export const EXPANSION_FIELDS = Object.freeze([
  { key: 'palette', vocab: V.COLOR, list: false, required: true },
  { key: 'typeScale', vocab: V.TYPE_SCALE, list: false, required: true },
  { key: 'radius', vocab: V.RADIUS_TAG, list: false, required: true },
  { key: 'elevation', vocab: V.ELEVATION_TAG, list: false, required: true },
  { key: 'density', vocab: V.SPACING, list: false, required: true },
  { key: 'effects', vocab: V.EFFECTS, list: true, required: true },
]);

/** Identity fields every genome must carry. */
export const IDENTITY_FIELDS = Object.freeze([
  { key: 'id', required: true },
  { key: 'name', required: true },
  { key: 'family', vocab: V.FAMILIES, list: false, required: true },
]);
